import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type {
  CollabConnectionState,
  PresenceParticipant,
} from '@/lib/realtime'

interface CollabPresenceProps {
  presence: PresenceParticipant[]
  selfSessionId: string | null
  connectionState: CollabConnectionState
  lastError: string | null
  enabled: boolean
}

const MAX_VISIBLE_PILLS = 4

/**
 * Avatar pills + connection status badge for a single document room.
 *
 * Renders nothing when the realtime feature is disabled — keeps the
 * document header layout identical to pre-collab builds for users on
 * environments where the flag hasn't flipped on yet.
 *
 * The connection-state badge is the single source of truth for
 * "your edits are flowing" feedback. The states map to user-facing
 * copy that's actionable: "Offline — edits will save when you
 * reconnect" rather than a generic spinner.
 */
export function CollabPresence({
  presence,
  selfSessionId,
  connectionState,
  lastError,
  enabled,
}: CollabPresenceProps) {
  if (!enabled) return null

  const others = presence.filter((p) => p.session_id !== selfSessionId)
  const visible = others.slice(0, MAX_VISIBLE_PILLS)
  const hiddenCount = Math.max(0, others.length - MAX_VISIBLE_PILLS)

  return (
    <div
      className="flex items-center gap-2"
      data-testid="collab-presence"
      data-collab-state={connectionState}
    >
      {visible.length > 0 && (
        <TooltipProvider>
          <div className="flex -space-x-2">
            {visible.map((participant) => (
              <Tooltip key={participant.session_id}>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      'inline-flex size-7 items-center justify-center rounded-full border-2 border-background',
                      'text-[10px] font-semibold uppercase tracking-tight',
                      avatarColor(participant.user_id),
                    )}
                    data-testid={`collab-pill-${participant.session_id}`}
                  >
                    {initialsOf(participant.display)}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {participant.display}
                </TooltipContent>
              </Tooltip>
            ))}
            {hiddenCount > 0 && (
              <span
                className="inline-flex size-7 items-center justify-center rounded-full border-2 border-background bg-muted text-[10px] font-semibold text-muted-foreground"
                data-testid="collab-pill-overflow"
              >
                +{hiddenCount}
              </span>
            )}
          </div>
        </TooltipProvider>
      )}
      <CollabStatusBadge
        connectionState={connectionState}
        lastError={lastError}
      />
    </div>
  )
}

function CollabStatusBadge({
  connectionState,
  lastError,
}: {
  connectionState: CollabConnectionState
  lastError: string | null
}) {
  const { label, tone, hint } = describeConnection(connectionState, lastError)
  if (label === null) return null
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium',
              tone,
            )}
            data-testid="collab-status"
            data-connection-state={connectionState}
            aria-live="polite"
          >
            <span
              aria-hidden
              className={cn(
                'inline-block size-1.5 rounded-full',
                connectionState === 'connected'
                  ? 'bg-emerald-500'
                  : connectionState === 'reconnecting' ||
                      connectionState === 'connecting'
                    ? 'bg-amber-500 animate-pulse'
                    : 'bg-red-500',
              )}
            />
            {label}
          </span>
        </TooltipTrigger>
        {hint && <TooltipContent side="bottom">{hint}</TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  )
}

function describeConnection(
  state: CollabConnectionState,
  lastError: string | null,
): { label: string | null; tone: string; hint: string | null } {
  switch (state) {
    case 'connected':
      // Hide the badge when fully online — the avatar pills already
      // communicate "you're collaborating live". Showing a green dot
      // forever would be visual noise.
      return { label: null, tone: '', hint: null }
    case 'connecting':
      return {
        label: 'Connecting…',
        tone: 'bg-amber-50 text-amber-800',
        hint: 'Joining the live editing session.',
      }
    case 'reconnecting':
      return {
        label: 'Reconnecting…',
        tone: 'bg-amber-50 text-amber-800',
        hint: 'Lost connection — your edits will save when we reconnect.',
      }
    case 'disconnected':
      return {
        label: 'Offline',
        tone: 'bg-red-50 text-red-800',
        hint: 'Realtime sync is offline. Your edits are still saving via REST.',
      }
    case 'error':
      return {
        label: lastError === 'forbidden' ? 'Read only' : 'Sync error',
        tone: 'bg-red-50 text-red-800',
        hint:
          lastError === 'forbidden'
            ? "You don't have edit access; changes are disabled."
            : `Realtime sync failed (${lastError ?? 'unknown'}). REST save still works.`,
      }
    case 'idle':
    default:
      return { label: null, tone: '', hint: null }
  }
}

/** Hash a stable string to one of a small palette so the same user reliably
 *  gets the same pill colour across renders / tabs. */
function avatarColor(seed: string): string {
  // 6 colour bands chosen for AA contrast on the white badge surface.
  const palette = [
    'bg-rose-500 text-white',
    'bg-amber-500 text-white',
    'bg-emerald-500 text-white',
    'bg-sky-500 text-white',
    'bg-violet-500 text-white',
    'bg-fuchsia-500 text-white',
  ]
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  }
  return palette[hash % palette.length] ?? palette[0] ?? 'bg-slate-500 text-white'
}

function initialsOf(display: string): string {
  const trimmed = display.trim()
  if (!trimmed) return '??'
  const parts = trimmed.split(/\s+/).filter(Boolean)
  if (parts.length === 1) {
    return (parts[0] ?? '').slice(0, 2).toUpperCase()
  }
  return `${(parts[0] ?? '').charAt(0)}${(parts[parts.length - 1] ?? '').charAt(0)}`.toUpperCase()
}
